import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { UserService } from '../services/user.service';
import { DynamicFormComponent } from '../../../utils/dynamic-form/dynamic-form.component';
import { ActionApiKey, ActionConfig, FieldConfig, FieldType } from '../../../utils/dynamic-form/form.type';
import * as _ from 'lodash';
import { TagModule } from 'primeng/tag';
import { KeyStatus } from '../models/user.utils';
import { Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-user-detail',
  standalone: true,
  imports: [ButtonModule, AvatarModule, DynamicFormComponent, TranslateModule, TagModule, ToastModule],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.scss',
  providers: [TranslateService, MessageService],
})
export class UserDetailComponent implements OnInit {
  @Input() editable = false;
  @Input() data;
  @Output() changed = new EventEmitter<string>();
  @Output() closeDialog = new EventEmitter<string>();
  @Output() formValid = new EventEmitter<boolean>();
  fieldsEdit: FieldConfig[] = [];
  fieldsView: FieldConfig[] = [];
  user: any;
  buttonEnabled = false;
  
  constructor(
    private readonly translate: TranslateService,
    private readonly userService: UserService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.user = structuredClone(this.data);
    this.initializeFields();
  }

  private initializeFields(): void {
    // Common fields for both seller and non-seller
    const commonEditFields: Partial<FieldConfig>[] = [
      this.createFieldConfig('firstname', 'user.firstname', this.user?.firstname, FieldType.input, {
        visibleLabel: true,
        alphabeticOnly: true,
        validation: [Validators.required, Validators.pattern(/^\s*\S.*$/)],
        iconPrefix: 'pi pi-user',
      }),
      this.createFieldConfig('lastname', 'user.lastname', this.user?.lastname, FieldType.input, {
        visibleLabel: true,
        alphabeticOnly: true,
        validation: [Validators.required, Validators.pattern(/^\s*\S.*$/)],
        iconPrefix: 'pi pi-user',
      }),
      this.createFieldConfig('defaultRole', 'user.defaultRole', this.user?.defaultRole?.roleName, FieldType.input, {
        disabled: true,
        visibleLabel: true,
      }),
      this.createFieldConfig('email', 'user.email', this.user?.email, FieldType.input, {
        disabled: true,
        visibleLabel: true,
        iconPrefix: 'pi pi-envelope',
      }),
      this.createFieldConfig('defaultLanguage', 'user.defaultLanguage', this.user.defaultLanguage, FieldType.select, {
        options: this.user?.relatedLanguages,
        optionLabel: 'languageName',
        visibleLabel: true,
      }),
    ];

    const commonViewFields: Partial<FieldConfig>[] = [
      this.createFieldConfig('firstname', 'user.firstname', this.user?.firstname),
      this.createFieldConfig('lastname', 'user.lastname', this.user?.lastname),
      this.createFieldConfig('defaultRole', 'user.defaultRole', this.user?.defaultRole?.roleName, undefined, {
        tooltip: this.user?.defaultRole?.roleDescription,
      }),
      this.createFieldConfig('email', 'user.email', this.user?.email),
      this.createFieldConfig('defaultLanguage', 'user.defaultLanguage', this.user?.defaultLanguage?.languageName),
    ];

    if (this.userService.isSeller()) {
      this.initializeSellerFields(commonEditFields, commonViewFields);
    } else {
      this.initializeNonSellerFields(commonEditFields, commonViewFields);
    }
  }

  private initializeSellerFields(commonEditFields: Partial<FieldConfig>[], commonViewFields: Partial<FieldConfig>[]): void {
    // Additional fields for seller in edit mode
    const sellerEditFields: Partial<FieldConfig>[] = [
      this.createFieldConfig('phoneNumber', 'user.phoneNumber', this.user?.phoneNumber, FieldType.input, {
        visibleLabel: true,
        numericOnly: true,
        validation: [Validators.required, Validators.pattern(/^\+?[1-9]\d{1,14}$/)],
      }),
      this.createFieldConfig('password', 'user.password', 'user.sendActivationLink', FieldType.button, {
        subType: 'text',
        iconPrefix: 'pi pi-envelope',
        visibleLabel: true,
        visible: 'edit',
      }),
      this.createFieldConfig('defaultShop', 'shop.shop', this.user.defaultShop, FieldType.select, {
        options: this.user.relatedShops,
        optionLabel: 'shopName',
        visibleLabel: true,
        composite: true,
      }),
    ];

    // Additional fields for seller in view mode
    const sellerViewFields: Partial<FieldConfig>[] = [
      this.createFieldConfig('phoneNumber', 'user.phoneNumber', this.user?.phoneNumber),
      this.createFieldConfig('password', 'user.password', '***********'),
      this.createFieldConfig('legalEntity', 'user.legalEntity', this.user?.legalEntityName),
      this.createFieldConfig('defaultShop', 'user.defaultShop', 
        this.user?.defaultShop?.shopName + ' - ' + this.user.defaultShop?.shopChannel?.channelName),
      this.createFieldConfig('apiKey', 'user.apiKey', 
        'keyStatus.' + this.user?.keyStatus || 'keyStatus.' + KeyStatus.non_activee, undefined, {
          customStyle: true,
          status: this.user?.keyStatus ?? KeyStatus.non_activee,
          actionButton: this.showActionButton(this.user?.keyStatus),
        }),
    ];

    this.fieldsEdit = [...commonEditFields, ...sellerEditFields] as FieldConfig[];
    this.fieldsView = [...commonViewFields, ...sellerViewFields] as FieldConfig[];
  }

  private initializeNonSellerFields(commonEditFields: Partial<FieldConfig>[], commonViewFields: Partial<FieldConfig>[]): void {
    // Modify common fields for non-seller
    commonEditFields[0].disabled = true; // firstname
    commonEditFields[1].disabled = true; // lastname

    // Additional field for non-seller in edit mode
    const nonSellerEditFields: Partial<FieldConfig>[] = [
      this.createFieldConfig('defaultChannel', 'user.defaultChannel', this.user?.defaultChannel, FieldType.select, {
        options: this.user?.relatedChannels,
        optionLabel: 'channelName',
        visibleLabel: true,
      }),
    ];

    // Additional field for non-seller in view mode
    const nonSellerViewFields: Partial<FieldConfig>[] = [
      this.createFieldConfig('defaultChannel', 'user.defaultChannel', this.user?.defaultChannel?.channelName),
    ];

    this.fieldsEdit = [...commonEditFields, ...nonSellerEditFields] as FieldConfig[];
    this.fieldsView = [...commonViewFields, ...nonSellerViewFields] as FieldConfig[];
  }

  /**
   * Helper method to create field configuration objects
   */
  private createFieldConfig(
    name: string, 
    label: string, 
    value: any, 
    type?: FieldType, 
    additionalProps: Partial<FieldConfig> = {}
  ): Partial<FieldConfig> {
    return {
      name,
      label,
      value,
      type,
      ...additionalProps
    };
  }

  showActionButton(status: string): ActionConfig {
    let actionConfig: ActionConfig = {
      isDisplayed: false,
    };
    if (status === KeyStatus.non_activee) {
      actionConfig.type = ActionApiKey.activate;
      actionConfig.isDisplayed = true;
      return actionConfig;
    } else if (status === KeyStatus.activee) {
      actionConfig.type = ActionApiKey.regenerate;
      actionConfig.isDisplayed = true;
      return actionConfig;
    }
    return actionConfig;
  }
  
  detectFormState(event) {
    _.merge(this.user, event);
    this.changed.emit(this.user);
  }

  formChange(event) {
    this.buttonEnabled = event.valid;
    this.formValid.emit(this.buttonEnabled)
  }

  handleAction(event) {
    this.userService.resetPassword(this.user.email).subscribe({
      next: () => {
        this.closeDialog.emit();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('messages.error'),
          detail: this.translate.instant('messages.errorSummary'),
          life: 3000,
        });
      }
    })
  }
}